import type {DesignModuleSpecJson} from '../data/contracts';
import type {TemplateNode,TemplateMeasure} from '@/uptk/core/components/moduleEntity/template/templateTypes';
import {entitiesOf,numericTypes} from './templateBindings';
import {fieldOptions} from './calculatedBindings';
import type {ScreenBindingProposal} from './templateScreenBindings';

export type GroupEvidence={collection:string;groupBy:string;filterField:string;filterValue:string;operation:'sum';source:string};
/** Recognize the literal partition/reduction idiom; never execute imported JavaScript. */
export function readGroupEvidence(source:string):GroupEvidence[]{
 if(source.length>5*1024*1024)return [];
 const pattern=/const\s+groups\s*=\s*\w+\.map\(label\s*=>\s*\(\{label,value:state\.(\w+)\.filter\(x\s*=>\s*x\.(\w+)\s*===\s*'([^']{1,120})'\s*&&\s*x\.(\w+)\s*===\s*label\)\.reduce\(\(s,x\)\s*=>\s*s\+monthly\(x\),0\)\}\)\)/g;
 return [...source.matchAll(pattern)].slice(0,8).map(m=>({collection:m[1],filterField:m[2],filterValue:m[3],groupBy:m[4],operation:'sum',source:m[0]}));
}
export function groupedBarTemplate(measure:TemplateMeasure):TemplateNode {
 const entity=measure.entity,field=measure.field??measure.groupBy!;
 const value=(projection:'value'|'share'):TemplateNode=>({type:'field',entity,field,scope:'item',groupValue:projection,format:{kind:'number',decimals:projection==='share'?1:2}});
 return {type:'repeat',source:{...measure,limit:50},empty:{type:'text',token:'—'},item:{type:'el',tag:'div',children:[
  {type:'el',tag:'div',cls:['between'],children:[{type:'field',entity,field:measure.groupBy!,scope:'item'},value('value')]},
  {type:'el',tag:'div',cls:['progress'],children:[{type:'el',tag:'span',style:{background:'var(--accent)',width:{bind:{type:'field',entity,field,scope:'item',groupValue:'share'},unit:'%',range:{min:0,max:100}}},children:[]}]},
  {type:'el',tag:'span',children:[value('share'),{type:'text',token:' %'}]}
 ]}};
}
/** A proved source and an unambiguous model are both required. Review owns acceptance. */
export function proposeGroupedBindings(spec:DesignModuleSpecJson,root:TemplateNode,messages:Record<string,Record<string,string>>,evidence:GroupEvidence[]):ScreenBindingProposal[]{
 const proposals:ScreenBindingProposal[]=[];
 for(const proof of evidence){
  const choices=Object.entries(entitiesOf(spec)).flatMap(([entity,model])=>{
   const group=model.fields.find(f=>f.name===proof.groupBy&&['select','text'].includes(f.type));
   const filter=model.fields.find(f=>f.name===proof.filterField);
   const amounts=model.fields.filter(f=>numericTypes.has(f.type)&&/monthlyequivalent|monthlyamount/i.test(f.name));
   return group&&filter&&fieldOptions(filter).some(o=>o.value===proof.filterValue)&&amounts.length===1?[{entity,group,amount:amounts[0]}]:[];
  });
  if(choices.length!==1)continue;
  const {entity,group,amount}=choices[0],labels=[...new Set(fieldOptions(group).flatMap(o=>[o.value,...(typeof o.label==='object'?Object.values(o.label):[o.label??''])]))];
  const text=(n:TemplateNode):string=>n.type==='text'?(messages[n.i18n??'']?.fr??n.token??''):n.type==='el'?n.children.map(text).join(' '):'';
  const candidates:{node:TemplateNode;path:(string|number)[]}[]=[];
  const walk=(n:TemplateNode,path:(string|number)[])=>{
   if(n.type!=='el')return;
   const matches=labels.filter(label=>label.length>2&&text(n).includes(label));
   if(matches.length>=2)candidates.push({node:n,path});
   n.children.forEach((child,i)=>walk(child,[...path,'children',i]));
  };walk(root,[]);
  const deepest=candidates.filter(c=>!candidates.some(other=>other.path.length>c.path.length&&c.path.every((part,i)=>other.path[i]===part)));
  if(deepest.length!==1)continue;
  let path=deepest[0].path;
  // A captured CSS donut has no chart node: replace its whole visual with governed bars.
  const parentPath=path.slice(0,-2);let parent:unknown=root;for(const part of parentPath)parent=(parent as Record<string|number,unknown>)[part];
  if((parent as {cls?:string[]}).cls?.includes('donut-wrap'))path=parentPath;
  const measure:TemplateMeasure={entity,field:amount.name,groupBy:group.name,measure:'sum',filters:[{field:proof.filterField,value:proof.filterValue}]};
  proposals.push({path,node:groupedBarTemplate(measure),sample:group.name,context:JSON.stringify({evidence:proof,measure}),confidence:.95,reason:'repeat'});
 }
 return proposals;
}
